import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState/EmptyState';
import Footer from '../../components/Footer/Footer';
import LogModal from '../../components/Logs/LogModal';
import Menu from '../../components/Menu/Menu';
import Pagination from '../../components/Pagination/Pagination';
import Toast from '../../components/Toast/Toast';
import {
    deleteMonitor,
    getMonitors,
    startMonitor,
    stopMonitor,
} from '../../services/MonitorsService';
import { confirmAction, confirmDelete } from '../../utils/confirmDialog';
import MonitorModal from './MonitorModal/MonitorModal';
import MonitorRow from './MonitorRow';
import NewMonitorButton from './NewMonitorButton';

function Monitors() {
    const defaultLocation = useLocation();
    const navigate = useNavigate();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        setPage(getPage(defaultLocation));
    }, [defaultLocation]);

    const [monitors, setMonitors] = useState([]);

    const [count, setCount] = useState(0);

    const [notification, setNotification] = useState({ type: '', text: '' });

    const [editMonitor, setEditMonitor] = useState({});

    const [page, setPage] = useState(getPage());

    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        getMonitors(page || 1)
            .then(result => {
                setMonitors(result.rows);
                setCount(result.count);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }, [page, refresh]);

    function onEditClick(event) {
        const id = event.target.id.replace('edit', '');
        const monitor = monitors.find(m => m.id == id);
        setEditMonitor({ ...monitor });
    }

    function onViewClick(event) {
        const id = event.target.id.replace('view', '');
        navigate('/monitors/' + id);
    }

    function onLogsClick(event) {
        const id = event.target.id.replace('logs', '');
        setEditMonitor(monitors.find(m => m.id == id));
    }

    function onStopClick(event) {
        const id = event.target.id.replace('stop', '');
        confirmAction({
            text: 'This monitor is running and will be stopped!',
            confirmButtonText: 'Yes, stop it!',
        }).then(confirmed => {
            if (!confirmed) return;
            stopMonitor(id)
                .then(() => setRefresh(Date.now()))
                .catch(err => {
                    console.error(
                        err.response ? err.response.data : err.message,
                    );
                    setNotification({
                        type: 'error',
                        text: err.response ? err.response.data : err.message,
                    });
                });
        });
    }

    function onStartClick(event) {
        const id = event.target.id.replace('start', '');
        startMonitor(id)
            .then(() => setRefresh(Date.now()))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        confirmDelete('This monitor will be permanently deleted!').then(
            confirmed => {
                if (!confirmed) return;
                deleteMonitor(id)
                    .then(() => setRefresh(Date.now()))
                    .catch(err => {
                        console.error(
                            err.response ? err.response.data : err.message,
                        );
                        setNotification({
                            type: 'error',
                            text: err.response
                                ? err.response.data
                                : err.message,
                        });
                    });
            },
        );
    }

    function onMonitorSubmit(order) {
        setRefresh(Date.now());
    }

    function onRefreshClick(event) {
        setRefresh(Date.now());
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4">Monitors</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <button
                                type="button"
                                className="btn btn-gray-800 d-inline-flex align-items-center me-2"
                                onClick={onRefreshClick}
                                title="Reload the monitors list"
                            >
                                <FontAwesomeIcon
                                    icon={faRedo}
                                    className="icon icon-xs me-2"
                                />
                                Refresh
                            </button>
                            <NewMonitorButton />
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th className="border-gray-200">Type</th>
                                <th className="border-gray-200">Symbol</th>
                                <th className="border-gray-200">Indexes</th>
                                <th className="border-gray-200">Status</th>
                                <th className="border-gray-200">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monitors && monitors.length ? (
                                monitors.map(monitor => (
                                    <MonitorRow
                                        key={monitor.id}
                                        data={monitor}
                                        onEditClick={onEditClick}
                                        onViewClick={onViewClick}
                                        onStartClick={onStartClick}
                                        onStopClick={onStopClick}
                                        onDeleteClick={onDeleteClick}
                                        onLogsClick={onLogsClick}
                                    />
                                ))
                            ) : (
                                <EmptyState
                                    colSpan={5}
                                    title="No monitors yet"
                                    description="Create your first monitor to watch market indexes in real time."
                                    actionLabel="Create your first monitor"
                                    actionAttrs={{
                                        'data-bs-toggle': 'modal',
                                        'data-bs-target': '#modalMonitor',
                                    }}
                                />
                            )}
                        </tbody>
                    </table>
                    <Pagination count={count} />
                </div>
                <Footer />
            </main>
            <MonitorModal data={editMonitor} onSubmit={onMonitorSubmit} />
            <LogModal file={editMonitor.id > 0 ? 'M' + editMonitor.id : ''} />
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default Monitors;
